import {
  CaretLeftIcon,
  ConfettiIcon,
  CrosshairIcon,
  FilesIcon,
  FloppyDiskIcon,
  HourglassHighIcon,
  SwatchesIcon,
  TranslateIcon,
  UsersThreeIcon,
} from "@phosphor-icons/react";
import { Checkbox, Input } from "@libertify/ui";
import {
  useCustomMutation,
  useGo,
  useInvalidate,
  useOne,
  useTranslate,
} from "@refinedev/core";
import { Card, Flex, Form, Modal, Result, Spin, Tag } from "antd";
import PageHeader from "components/PageHeader/";
import { ShadowButton, TrackedShadowButton } from "components/ShadowButton";
import { useAntTheme } from "hooks/useAntTheme";
import { BrandKitPreview } from "pages/media/components/BrandKitPreview";
import { secToHHMMSS } from "pages/media/utils";
import useOnboardingAnswers from "pages/onboarding/hooks/useOnboardingAnswers";
import { useCallback, useMemo, useState } from "react";
import { useVolatileBoundStore } from "store";
import { AnalyticsEvents } from "types/analytics";
import { BrandKit, ContactResponse, MediaProjectResponse } from "../../types";
import { useProjectNavigate } from "../hooks/useProjectNavigate";
import { useAnalytics } from "hooks/useAnalytics";
import { Step } from "./ProjectCreateWizard";
import { ProjectSettingsFormBrandKit } from "./ProjectSettingsFormSteps/ProjectSettingsFormBrandKit";
import { ProjectSettingsFormDuration } from "./ProjectSettingsFormSteps/ProjectSettingsFormDuration";
import { ProjectSettingsFormLanguage } from "./ProjectSettingsFormSteps/ProjectSettingsFormLanguage";
import { ProjectSettingsFormSelectedPages } from "./ProjectSettingsFormSteps/ProjectSettingsFormSelectedPages";
import { SettingsItem } from "./SettingsItem";
import SpeakerView from "./SpeakerView";
import { convertProjectDocumentsToUploadFiles } from "../helpers/project-create-wizard";
import { ObjectivesSelect } from "./ObjectivesSelect";
import { ObjectivesFormItem } from "./ObjectivesFormItem";

type SettingValue = string | number | null | undefined;
export const SettingsApprovalScreen = ({
  projectId,
  previousStep,
  storyboardId,
  instanceId,
  stepNumber,
  stepName,
  playbookName,
}: {
  projectId: string;
  previousStep: () => void;
  storyboardId: string;
  instanceId: string;
  stepNumber?: number;
  stepName?: string;
  playbookName?: string;
}) => {
  const t = useTranslate();
  const { mutateAsync: approve, isPending: isApproving } = useCustomMutation(
    {}
  );
  const [form] = Form.useForm();
  const go = useGo();
  const invalidate = useInvalidate();
  const { theme } = useAntTheme();
  const { navigateToStoryboard } = useProjectNavigate();
  const { trackEvent } = useAnalytics();

  const { shouldGoToOnboarding, isLoading: isOnboardingDataLoading } =
    useOnboardingAnswers();
  const { setCurrentProjectId } = useVolatileBoundStore((state) => ({
    setCurrentProjectId: state.setCurrentProjectId,
  }));

  const [updatedValue, setUpdatedValue] = useState<SettingValue>(undefined);
  // Captured alongside `updatedValue` for the Contact step: a contact with no
  // face profile must run as an audio-only experience.
  const [isVoiceOnly, setIsVoiceOnly] = useState<boolean | null>(null);
  const [step, setStep] = useState<Step | undefined>(undefined);
  const [saveSettingsChecked, setSaveSettingsChecked] = useState(false);
  const [savedSettingsName, setSavedSettingsName] = useState("");
  const goToOnboardingOrProject = () => {
    if (shouldGoToOnboarding) {
      setCurrentProjectId(projectId);
      go({
        to: "/onboarding",
      });
    } else {
      navigateToStoryboard({
        projectId,
        storyboardId,
        instanceId,
        subRoute: "generating",
      });
    }
  };

  const { data: projectData, isLoading: isProjectLoading } =
    useOne<MediaProjectResponse>({
      resource: "media/projects",
      id: projectId,
    });

  const organizationId = projectData?.data.organization_id;
  const contactId = projectData?.data.settings.contact_id;
  const brandKitId = projectData?.data.settings.brand_kit_id;

  const { data: contactData, isLoading: isContactLoading } =
    useOne<ContactResponse>({
      resource: `media/${organizationId}/contacts`,
      id: contactId ?? undefined,
      queryOptions: {
        enabled: !!organizationId && !!contactId,
      },
    });

  const { data: brandKitData, isLoading: isBrandKitLoading } = useOne<BrandKit>(
    {
      resource: `media/${organizationId}/brand_kits`,
      id: brandKitId ?? undefined,
      queryOptions: {
        enabled: !!organizationId && !!brandKitId,
      },
    }
  );

  const isDataLoading =
    isProjectLoading ||
    (contactId && (isContactLoading || !contactData?.data)) ||
    (brandKitId && (isBrandKitLoading || !brandKitData?.data));

  const storyboard = projectData?.data.storyboards.find(
    (storyboard) => storyboard.id === storyboardId
  );

  const [userSelectedObjectives, setUserSelectedObjectives] = useState<
    string[] | null
  >(null);

  const selectedObjectives = useMemo(
    () => userSelectedObjectives ?? storyboard?.objectives ?? [],
    [userSelectedObjectives, storyboard?.objectives]
  );

  const objectivesContent: string = useMemo(() => {
    const content = [
      storyboard?.brief,
      ...(storyboard?.objectives ?? []),
    ].filter(Boolean);

    if (content.length === 0)
      return t("projects.components.SettingsApproval.noObjectives");
    return content.join(", ");
  }, [storyboard?.brief, storyboard?.objectives, t]);

  const instanceSettingsSteps = useMemo(
    () => ["CallToActions", "BrandKit", "Contact", "Transitions", "Experience"],
    []
  );

  const settings = projectData?.data.settings;
  const { language, duration, selected_pages, brand_kit_id, contact_id } =
    settings ?? {};

  const { mutateAsync, isPending: isMutateLoading } = useCustomMutation();

  const handleSettingsChange = useCallback(
    async (step: Step, value: SettingValue) => {
      const STEP_TO_FIELD_MAP: Record<string, string> = {
        Objectives: "objectives",
        Language: "language",
        Pages: "selected_pages",
        Duration: "duration",
        BrandKit: "brand_kit_id",
        Contact: "contact_id",
      };
      const getValues = () => {
        if (step === "Objectives") {
          return {
            brief: value,
            objectives: selectedObjectives,
          };
        }
        if (step === "Contact") {
          return {
            contact_id: value,
            // Keep audio/video mode aligned with the picked contact's nature.
            ...(isVoiceOnly !== null && {
              media_generation_mode: isVoiceOnly ? "Audio" : "Video",
            }),
          };
        }
        const field = STEP_TO_FIELD_MAP[step];
        return field ? { [field]: value } : {};
      };
      const values = getValues();
      const instanceSettingsUrl = `media/projects/${projectId}/instances/${instanceId}/settings`;
      const storyboardSettingsUrl = `media/projects/${projectId}/storyboards/${storyboardId}/settings`;
      const url = instanceSettingsSteps.includes(step)
        ? instanceSettingsUrl
        : storyboardSettingsUrl;

      await mutateAsync({
        url,
        method: "patch",
        values,
      });

      await invalidate({
        resource: "media/projects",
        invalidates: ["detail"],
        id: projectId,
      });
    },
    [
      mutateAsync,
      invalidate,
      projectId,
      instanceId,
      storyboardId,
      selectedObjectives,
      instanceSettingsSteps,
      isVoiceOnly,
    ]
  );

  const handleConfirm = () => {
    void (async () => {
      if (stepNumber && stepName) {
        trackEvent(AnalyticsEvents.ProjectSettingUpdate, {
          project_id: projectId,
          setting_name: `wizard_step_${stepNumber}_${stepName}`,
          setting_value: "completed",
          page: "project_creation_wizard",
        });
      }

      // Save settings as a template if the user checked the option
      if (saveSettingsChecked && savedSettingsName.trim() && organizationId) {
        await mutateAsync({
          url: `media/${organizationId}/saved-settings`,
          method: "post",
          values: {
            name: savedSettingsName.trim(),
            storyboard_settings: {
              language,
              duration,
              selected_pages: selected_pages ?? "all",
            },
            media_settings: {
              brand_kit_id,
              contact_id,
            },
          },
        });
      }

      const success = await approve({
        url: `media/projects/${projectId}/settings/${storyboardId}/approve`,
        method: "post",
        values: {},
      });

      if (success) {
        await invalidate({
          resource: "media_projects",
          invalidates: ["detail"],
          id: projectId,
        });
        goToOnboardingOrProject();
      }
    })();
  };

  if (isDataLoading || !projectData?.data) {
    return (
      <Flex justify="center" align="center" style={{ height: "100%" }}>
        <Spin size="large" />
      </Flex>
    );
  }

  const projectUploadedFiles = convertProjectDocumentsToUploadFiles(
    projectData.data.documents
  );

  return (
    <Flex vertical align="center" style={{ marginTop: -20 }}>
      <Modal
        open={!!step}
        width={"85vw"}
        onCancel={() => setStep(undefined)}
        onOk={() => {
          if (!step) return;
          handleSettingsChange(step, updatedValue)
            .then(() => {
              setStep(undefined);
            })
            .catch((error) => {
              console.error(error);
            });
        }}
        confirmLoading={isMutateLoading}
        cancelButtonProps={{
          disabled: isMutateLoading,
        }}
        closable={!isMutateLoading}
        maskClosable={!isMutateLoading}
      >
        <>
          <Flex style={{ width: "100%" }} align="center" justify="center">
            <PageHeader
              title={t(`projects.components.ProjectCreateWizard.${step}`)}
              subtitle={t(
                `projects.components.ProjectCreateWizard.chooseThe${step}`
              )}
            />
          </Flex>
          {step === "Language" && (
            <ProjectSettingsFormLanguage
              value={updatedValue}
              onChange={setUpdatedValue}
            />
          )}
          {step === "Pages" && (
            <ProjectSettingsFormSelectedPages
              value={selected_pages}
              onChange={setUpdatedValue}
              uploadedFiles={projectUploadedFiles}
            />
          )}
          {step === "Duration" && (
            <ProjectSettingsFormDuration
              value={updatedValue}
              onChange={setUpdatedValue}
            />
          )}
          {step === "BrandKit" && (
            <ProjectSettingsFormBrandKit
              value={updatedValue}
              onChange={setUpdatedValue}
              project={projectData.data}
              instanceId={instanceId}
              isWizard={true}
            />
          )}
          {step === "Contact" && (
            <SpeakerView
              initialVoiceOnly={false}
              showMoreFields={false}
              project={projectData.data}
              showNavigation={false}
              showHeader={false}
              itemsPerSlide={3}
              onContactChange={(contactId, voiceOnly) => {
                setUpdatedValue(contactId);
                setIsVoiceOnly(voiceOnly);
              }}
            />
          )}
          {step === "Objectives" && (
            <Flex vertical gap={10}>
              <ObjectivesSelect
                selectedObjectives={selectedObjectives}
                storyboardSettings={storyboard}
                setUserSelectedObjectives={setUserSelectedObjectives}
              />
              <Form
                form={form}
                initialValues={{ description: storyboard?.brief }}
                layout="vertical"
                style={{
                  width: "100%",
                  textAlign: "center",
                  maxWidth: 900,
                  margin: "0 auto",
                }}
              >
                <ObjectivesFormItem onChange={setUpdatedValue} />
              </Form>
            </Flex>
          )}
        </>
      </Modal>
      <Result
        status="success"
        style={{ width: "100%" }}
        icon={<ConfettiIcon size={60} color={theme.colorPrimary} />}
        title={
          <Flex vertical align="center" gap={8}>
            {playbookName && (
              <Tag color={theme.colorPrimary}>
                {t("projects.components.SettingsApproval.playbookBadge", {
                  name: playbookName,
                })}
              </Tag>
            )}
            {t("projects.components.SettingsApproval.yourCustomRequirements")}
          </Flex>
        }
        subTitle={
          <Flex vertical gap={30}>
            <Card
              style={{
                backgroundColor: "transparent",
                border: "none",
              }}
            >
              <Flex
                vertical
                gap={24}
                align="center"
                justify="center"
                style={{ width: "70%", maxWidth: 700, margin: "auto" }}
              >
                <SettingsItem
                  title={t(
                    "projects.components.ProjectCreateWizard.step.Objectives"
                  )}
                  icon={<CrosshairIcon size={24} />}
                  content={objectivesContent}
                  onClick={() => {
                    setStep("Objectives");
                    setUpdatedValue(storyboard?.brief);
                  }}
                  alwaysShowEditIcon
                  editable
                />
                <SettingsItem
                  title={t("projects.components.SettingsApproval.language")}
                  icon={<TranslateIcon size={24} />}
                  content={language}
                  onClick={() => {
                    setStep("Language");
                    setUpdatedValue(language);
                  }}
                  alwaysShowEditIcon
                  editable
                />
                <SettingsItem
                  title={t("projects.components.SettingsApproval.duration")}
                  icon={<HourglassHighIcon size={24} />}
                  content={
                    duration && duration > 0
                      ? secToHHMMSS(duration)
                      : t("projects.components.SettingsApproval.durationAuto")
                  }
                  editable
                  alwaysShowEditIcon
                  onClick={() => {
                    setStep("Duration");
                    setUpdatedValue(duration);
                  }}
                />
                <SettingsItem
                  title={t("projects.components.SettingsApproval.contacts")}
                  icon={<UsersThreeIcon size={24} />}
                  content={`${contactData?.data.firstname} ${contactData?.data.lastname}`}
                  editable
                  alwaysShowEditIcon
                  onClick={() => {
                    setStep("Contact");
                    setUpdatedValue(contact_id);
                  }}
                />
                <SettingsItem
                  title={t("projects.components.SettingsApproval.pages")}
                  icon={<FilesIcon size={24} />}
                  content={
                    selected_pages ??
                    t("projects.components.ProjectSettings.keyPagesAll")
                  }
                  editable
                  alwaysShowEditIcon
                  onClick={() => {
                    setStep("Pages");
                    setUpdatedValue(selected_pages);
                  }}
                />
                <SettingsItem
                  title={t("projects.components.SettingsApproval.brandKit")}
                  icon={<SwatchesIcon size={24} />}
                  content={
                    brandKitData?.data ? (
                      <BrandKitPreview brandkit={brandKitData.data} />
                    ) : null
                  }
                  editable
                  alwaysShowEditIcon
                  onClick={() => {
                    setStep("BrandKit");
                    setUpdatedValue(brand_kit_id);
                  }}
                />
                <Flex vertical gap={12}>
                  <Flex
                    align="center"
                    gap={8}
                    style={{ width: "fit-content", margin: "auto" }}
                  >
                    <FloppyDiskIcon size={16} />
                    {t(
                      "projects.components.SettingsApproval.saveSettingsAsTemplate"
                    )}
                    <Checkbox
                      style={{ marginLeft: "auto" }}
                      checked={saveSettingsChecked}
                      onChange={(e) => {
                        setSaveSettingsChecked(e.target.checked);
                        if (!e.target.checked) {
                          setSavedSettingsName("");
                        }
                      }}
                    />
                  </Flex>
                  {saveSettingsChecked && (
                    <Input
                      placeholder={t(
                        "projects.components.SettingsApproval.settingsTemplateName"
                      )}
                      value={savedSettingsName}
                      onChange={(e) => setSavedSettingsName(e.target.value)}
                    />
                  )}
                </Flex>
              </Flex>
            </Card>
          </Flex>
        }
        extra={[
          <ShadowButton
            size="large"
            key="back"
            shape="round"
            icon={<CaretLeftIcon />}
            style={{ width: 130 }}
            onClick={() => previousStep()}
          >
            {t("buttons.back")}
          </ShadowButton>,
          <TrackedShadowButton
            trackingEventName={AnalyticsEvents.ProjectSettingsApprove}
            trackingProps={{ project_id: projectId }}
            size="large"
            key="approve"
            type="primary"
            shape="round"
            loading={isApproving || isOnboardingDataLoading}
            disabled={isApproving || isOnboardingDataLoading}
            onClick={handleConfirm}
          >
            {t("projects.components.SettingsApproval.approveSettings")}
          </TrackedShadowButton>,
        ]}
      />
    </Flex>
  );
};
